import _ from 'lodash'
import * as sentry from '../../src/utils/sentry'
import { IDB_smartDatasDelete, IDB_smartDatasReadAll, IDB_smartDatasWrite } from '../IDB/IDB.table.smartData'
import { IDB_smartDataReferencesReadAll, IDB_smartDataReferencesWrite } from '../IDB/IDB.table.smartDataReference'
import { SD_deleteSmartData } from './SD.deleteSmartData'
import { SD_extractSmartDataItems } from './SD.extractSmartDataItems'
import { SD_extractTargetReferences, SD_legacyTargetToReferenceId, type SD_TReference } from './SD.targets'
import type { SD_TSmartData } from './SD.types'
import { Serv_send } from '../Serv/Serv.socketClient'
import type { Sync_SyncResult } from '../Sync/Sync.master'

export async function SD_sync(data: Sync_SyncResult) {
    await addSmartData(data.response.smartData)

    // Update database with deletions
    await SD_deleteSmartData(data.response.deletedSmartData)

    await uploadSmartDataChanges()

    // Refresh smart data in flux store so that any ids being used in the UI are up to date with the
    // ids that are now in the local db.
    // refreshSmartData();
}

async function uploadSmartDataChanges() {
    const itemsP = IDB_smartDatasReadAll()
    const referencesP = IDB_smartDataReferencesReadAll()

    const [items, references] = await Promise.all([itemsP, referencesP])

    const promises = []

    for (const item of items) {
        // Skip all items without meta information
        if (item.meta === undefined) {
            continue
        }

        if (item.meta.new === true && item.meta.targets) {
            promises.push(syncNewSmartData(item, references))
        }

        if (item.meta.updated === true) {
            promises.push(syncUpdatedSmartData(item))
        }

        if (item.meta.delete === true) {
            try {
                promises.push(syncDeletedSmartData(item, references))
            } catch (error) {
                sentry.err(error, {})
            }
        }
    }

    // Returns a list of lists of all the target keys that need updating in the local db
    const targetKeyArrays = await Promise.all(promises)

    // Flatten & uniq to get final list of keys
    const allTargetKeys = _.flatten(targetKeyArrays)
    const targetKeys = _.uniq(allTargetKeys)
    const referencesToUpdate: SD_TReference[] = []

    // Find all the references that need to be updated
    for (const targetKey of targetKeys) {
        const reference = references.find((ref: any) => ref.reference === targetKey)

        // Should always be found, but be careful
        if (reference !== undefined) {
            referencesToUpdate.push(reference)
        }
    }

    // Update all the references that need updating
    if (references.length !== 0) {
        await IDB_smartDataReferencesWrite(referencesToUpdate)
    }
}

async function addSmartData(additions: Sync_SyncResult['response']['smartData']) {
    if (additions == null || additions.length === 0) return

    const references = await IDB_smartDataReferencesReadAll()

    const smartDataItems = SD_extractSmartDataItems(additions)
    const smartDataReferences = SD_extractTargetReferences(additions, references)

    // This does not account for the case there are unuploaded edits or deletions that may be overwritten
    await IDB_smartDatasWrite(smartDataItems)
    await IDB_smartDataReferencesWrite(smartDataReferences)
}

async function syncNewSmartData(smartData: any, references: any[]) {
    const localId = smartData.id

    const obj = {
        key: smartData.key,
        value: smartData.value,
        type: smartData.type,
        source: smartData.source,
        // For the moment we assume that there is only one target in the array. We want to support
        // mulitiple targets being stored in the local db but the server api doesn't deal with
        // specifying multiple targets and we only have a use case for one at the moment.
        targets: smartData.meta.targets[0],
    }

    const { id } = await Serv_send('v2.smartData.add', obj)

    // Add smart data object to database under its new id - this creates a duplicate of
    // the data as the object is already in the database. I don't know how to update the
    // object in place as we need the old id to reference it and the new id to update it
    // but we can only pass one id. So we add a new one here and delete the old one
    // afterwards

    // Add id from the server and save into the local db
    const localObj = {
        id,
        key: smartData.key,
        value: smartData.value,
        type: smartData.type,
        source: smartData.source,
        date: smartData.date,
    }

    await IDB_smartDatasWrite([localObj])

    // Search through the provided targets to find our localId and replace it with the
    // new server id
    const updateKeys = []

    for (const target of smartData.meta.targets) {
        const targetKey = SD_legacyTargetToReferenceId(target)
        const reference = references.find((ref) => ref.reference === targetKey)

        if (reference !== undefined) {
            const index = reference.ids.indexOf(localId)
            if (index !== -1) {
                reference.ids[index] = id
            }

            updateKeys.push(targetKey)
        }
    }

    // Delete the old entry for the smart data under the localId
    await IDB_smartDatasDelete([localId])

    return updateKeys
}

async function syncUpdatedSmartData(smartData: any): Promise<string[]> {
    // Create object to send to the server (we don't want the 'meta' data in there)
    const obj = {
        id: smartData.id,
        key: smartData.key,
        value: smartData.value,
    }

    // Update the server with new object
    await Serv_send('v2.smartData.edit', obj)

    const localObj: SD_TSmartData = {
        id: smartData.id,
        key: smartData.key,
        value: smartData.value,
        type: smartData.type,
        source: smartData.source,
        date: smartData.date,
    }

    // Update the local db with new object so that we don't have the meta data there any more
    await IDB_smartDatasWrite([localObj])

    return []
}

async function syncDeletedSmartData(smartData: any, references: any[]) {
    // Delete smart data on the server
    await Serv_send('v2.smartData.delete', { id: smartData.id })

    // Delete smart data from local db
    await IDB_smartDatasDelete([smartData.id])

    // Search through the provided targets to find our localId and replace it with the
    // new server id
    const updateKeys = []

    for (const target of smartData.meta.targets) {
        const targetKey = SD_legacyTargetToReferenceId(target)
        const reference = references.find((ref) => ref.reference === targetKey)

        if (reference !== undefined) {
            _.pull(reference.ids, smartData.id)

            updateKeys.push(targetKey)
        }
    }

    return updateKeys
}
